import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const JobDescriptionAnalyzerViewResult = ({ result }) => {
  const getQualityColor = (score) => {
    if (!score && score !== 0) return 'bg-base-200 text-base-content/70';
    if (score >= 0.8) return 'bg-success/20 text-success';
    if (score >= 0.6) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const formatSalary = (min, max, currency, period) => {
    // Return early if required values are missing
    if (!min || !max) return 'Not specified';
    
    // Ensure currency is a valid code, default to USD
    const validCurrency = (currency && /^[A-Z]{3}$/.test(currency)) ? currency : 'USD';
    
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: validCurrency });
    return `${formatter.format(min)} - ${formatter.format(max)}${period ? ` ${period}` : ''}`;
  };

  const posting = result?.posting_analysis || {};
  const metadata = result?.metadata || {};
  const extractedDetails = posting?.extracted_details || {};
  const company = extractedDetails?.company || {};
  const location = company?.location || {};
  const requirements = extractedDetails?.requirements || {};
  const compensation = extractedDetails?.compensation || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Job Details Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Briefcase size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Job Details</h3>
              </div>

              <div className="space-y-3">
                {/* Basic Info */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Job Title</span>
                      <span className="text-lg">{extractedDetails?.job_title || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Company</span>
                      <span>{company?.name || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Industry</span>
                      <span>{company?.industry || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Location</span>
                      <span>
                        {location?.city && location?.state 
                          ? `${location.city}, ${location.state}`
                          : 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Work Type</span>
                      <span className="capitalize">
                        {location?.remote_policy || 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Requirements */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Requirements</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-base-content/70">Experience: </span>
                      <span>{posting?.extracted_details?.requirements?.experience_level}</span>
                    </div>
                    <div>
                      <span className="text-base-content/70">Education:</span>
                      <div className="mt-1">
                        {Array.isArray(requirements?.education) && requirements.education.length > 0 ? (
                          requirements.education.map((edu, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.GraduationCap size={16} className="text-primary"/>
                              <span>{edu}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm">Not specified</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-base-content/70">Skills:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(requirements?.skills) && requirements.skills.length > 0 ? (
                          requirements.skills.map((skill, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs">
                              {skill}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm">Not specified</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compensation */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Compensation</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-base-content/70">Salary Range: </span>
                      <span className="font-medium">
                        {formatSalary(
                          posting?.extracted_details?.compensation?.salary_range?.min,
                          posting?.extracted_details?.compensation?.salary_range?.max,
                          posting?.extracted_details?.compensation?.salary_range?.currency,
                          posting?.extracted_details?.compensation?.salary_range?.period
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-base-content/70">Benefits:</span>
                      <div className="mt-1 space-y-1">
                        {Array.isArray(compensation?.benefits) && compensation.benefits.length > 0 ? (
                          compensation.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.Check className="text-success" size={14} />
                              <span className="capitalize">{benefit}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm">Not specified</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Quality Assessment Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Quality Assessment</h3>
              </div>

              <div className="space-y-3">
                {/* Content Quality */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Content Quality</h4>
                  <div className="space-y-2">
                    {Object.entries(posting?.content_quality || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        {typeof value === 'number' ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(value)}`}>
                            {Math.round(value * 100)}%
                          </span>
                        ) : (
                          <PhosphorIcons.Check 
                            className={value ? 'text-success' : 'text-error'} 
                            size={16} 
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Inclusivity Assessment */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Inclusivity Assessment</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span>Overall Score</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(posting?.inclusivity_assessment?.score)}`}>
                        {Math.round(posting?.inclusivity_assessment?.score * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Language Score</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(posting?.inclusivity_assessment?.inclusive_language_score)}`}>
                        {Math.round(posting?.inclusivity_assessment?.inclusive_language_score * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Improvement Suggestions */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Improvement Suggestions</h4>
                  {['critical', 'recommended', 'optional'].map(priority => {
                    const suggestions = result?.improvement_suggestions?.[priority];
                    return Array.isArray(suggestions) && suggestions.length > 0 ? (
                      <div key={priority} className="mt-2">
                        <span className={`text-sm capitalize ${
                          priority === 'critical' ? 'text-error' :
                          priority === 'recommended' ? 'text-warning' :
                          'text-info'
                        }`}>
                          {priority}:
                        </span>
                        <div className="mt-1 space-y-1">
                          {suggestions.map((suggestion, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.CaretRight size={16} />
                              <span>{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>

                {/* Analysis Metadata */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Analysis Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Analysis Quality</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success capitalize">
                        {metadata?.analysis_quality}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Confidence Score</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round(metadata?.confidence_score * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Validation Level</span>
                      <span className="capitalize">{metadata?.validation_level}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDescriptionAnalyzerViewResult;